import {
  type ITableCell,
  type ITableSettings,
  tableNormalizeColWidths,
} from "@library-elements/table";
import { type RootState, store } from "@store";
import { editEntity } from "@store/story";
import {
  getActiveEntityId,
  getEntityById,
  getTableActiveCellsIds,
  getTableActiveStartCellId,
} from "@store/story/selectors";
import { produce } from "immer";
import {
  handleTableOverwrites,
  syncTableOverwritesSnapshot,
} from "./handle-table-data-overwrites";
import { triggerContentUpdateFor } from "@/shared/utils/content-trigger-registry";

interface IOpts {
  entityId?: string;
  forceOverwritesOn?: (keyof ITableSettings)[];
  // Bypass the handleTableOverwrites cascade when the callback writes per-cell
  // (e.g. the borders popover), so cell-level writes aren't fanned table-wide.
  skipOverwrites?: boolean;
}

export const editTableEntity = (
  callback: (draft: ITableSettings, state: RootState) => void,
  opts: IOpts = {},
) => {
  const { entityId } = opts;
  const state = store.getState();

  const id = entityId || getActiveEntityId(state) || null;
  if (id == null) return;

  const entity = getEntityById(id)(state);
  if (entity?.settings.name !== "Table") {
    return;
  }
  const settings = entity.settings as unknown as ITableSettings;

  const data = produce(settings, (draft) => {
    callback(draft, state);
    if (!opts.skipOverwrites) {
      handleTableOverwrites({
        settings: draft,
        tableId: id,
        forceOverwritesOn: opts.forceOverwritesOn,
      });
    }

    if (!draft.tableColWidths) {
      draft.tableColWidths = [];
    }
    if (!draft.tableRowHeights) {
      draft.tableRowHeights = [];
    }

    if (draft.tableRowHeights.length > draft.tableRows) {
      draft.tableRowHeights = draft.tableRowHeights.slice(0, draft.tableRows);
    }

    if (draft.tableColWidths.length !== draft.tableCols) {
      const amtOfNewWidths = draft.tableCols - draft.tableColWidths.length;
      let newWidth = 100 / draft.tableCols;

      const newWidths = draft.tableColWidths.slice(0, draft.tableCols);

      if (draft.tableColWidths.length > 0) {
        newWidth = newWidths[newWidths.length - 1];
      }

      for (let i = 0; i < amtOfNewWidths; i++) {
        newWidths.push(newWidth);
      }

      draft.tableColWidths = tableNormalizeColWidths(newWidths);
    }
  });

  if (opts.skipOverwrites) {
    // The callback may still write table-level defaults (borders popover);
    // keep the overwrites snapshot current so the next non-skip edit doesn't
    // diff against stale values and reset the per-cell borders it just wrote.
    // Runs on the produced plain object, not the (revoked) immer draft.
    syncTableOverwritesSnapshot({ settings: data, tableId: id });
  }

  store.dispatch(
    editEntity({
      data: data as unknown as Record<string, unknown>,
      id: id,
    }),
  );
};

export const editTableCell = (
  callback: (draft: ITableCell, cellId: string, state: RootState) => void,
  opts: IOpts & { cellId?: string } = {},
) => {
  editTableEntity((draft, state) => {
    const cellId = opts.cellId ?? getTableActiveStartCellId(state);
    if (!cellId) return;

    const cell = draft.tableData.find((el) => el.id === cellId);
    if (!cell) return;
    callback(cell, cellId, state);
  }, opts);
};

export const editTableCells = (
  callback: (
    data: { cellIdx: number; cell: ITableCell },
    tableDraft: ITableSettings,
    state: RootState,
  ) => void,
  opts: IOpts & { skipActiveFroala?: boolean; cellsIds?: string[] } = {},
) => {
  editTableEntity((draft, state) => {
    const cellsIds = opts.cellsIds ?? getTableActiveCellsIds(state);

    const { tableData } = draft;

    for (let cellIdx = 0; cellIdx < tableData.length; cellIdx++) {
      const cell = tableData[cellIdx];
      if (!(opts.skipActiveFroala && cellsIds[0] === cell.id)) {
        if (cellsIds.includes(cell.id)) {
          callback({ cell, cellIdx }, draft, state);
        }
      }
    }
  }, opts);
};

export const setTableCellContent = (
  content: string,
  opts: IOpts & { cellId?: string } = {},
) => {
  editTableCell((draft, cellId, state) => {
    draft.content = content;

    const entityId = getActiveEntityId(state);
    triggerContentUpdateFor(`${entityId}-${cellId}`, content);
  }, opts);
};

export const tableClearActiveCells = (opts: IOpts = {}) => {
  editTableCells(({ cell }, _, state) => {
    cell.content = "";
    // Clearing a cell must also drop a formula, else the calc result keeps
    // rendering (its value lives in `calculation`, not `content`).
    cell.calculation = undefined;
    cell.calcFormatHtml = undefined;
    const entityId = getActiveEntityId(state);
    triggerContentUpdateFor(`${entityId}-${cell.id}`, "");
    // The active cell hosts a Froala instance keyed by the table id, not
    // per-cell. Without this, Froala keeps showing the old content until
    // the user deselects.
    const activeCellId = getTableActiveStartCellId(state);
    if (cell.id === activeCellId) {
      triggerContentUpdateFor(`${entityId}-table-froala`, "");
    }
  }, opts);
};
